import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { DeviceFrame } from "@/ui";
import type { ReplaySession } from "@/mock";

/** mm:ss from seconds. */
function clock(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Scripted "events" along the timeline so the scrubber has honest markers. */
function timelineMarkers(s: ReplaySession): { at: number; label: string; rage?: boolean }[] {
  const d = s.durationSec;
  const marks: { at: number; label: string; rage?: boolean }[] = [
    { at: 0.04, label: "Session start" },
    { at: 0.28, label: "Pageview" },
    { at: 0.52, label: "Scroll 60%" },
    { at: 0.74, label: "CTA click" },
  ];
  if (s.flag) marks.push({ at: 0.86, label: s.flag, rage: true });
  return marks.map((m) => ({ ...m, at: Math.round(m.at * d) }));
}

/** Skeleton wireframe of a mobile screen (PostHog mobile→rrweb comes as wireframes). */
function MobileWireframe({ progress }: { progress: number }) {
  const scroll = progress * 36; // px of faux scroll
  return (
    <div className="flex h-full flex-col">
      {/* status bar */}
      <div className="flex items-center justify-between px-3 pt-4 pb-1.5">
        <span className="font-mono text-[8px] text-subtle">9:41</span>
        <span className="h-1.5 w-6 rounded-full bg-ink-strong/15" />
      </div>
      <div
        className="flex flex-col gap-2.5 px-3 transition-transform duration-200 ease-out"
        style={{ transform: `translateY(-${scroll}px)` }}
      >
        {/* hero */}
        <div className="mt-1 h-16 rounded-lg bg-ink-strong/10" />
        <div className="h-2.5 w-3/4 rounded-full bg-ink-strong/12" />
        <div className="h-2.5 w-1/2 rounded-full bg-ink-strong/10" />
        {/* a coral CTA that "lights" past 70% */}
        <div
          className={
            "mt-1 h-7 rounded-lg transition-colors duration-200 " +
            (progress > 0.7 ? "bg-accent" : "bg-ink-strong/15")
          }
        />
        {/* sections */}
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="h-12 rounded-lg bg-ink-strong/8" />
          <div className="h-12 rounded-lg bg-ink-strong/8" />
        </div>
        <div className="h-2.5 w-2/3 rounded-full bg-ink-strong/10" />
        <div className="h-20 rounded-lg bg-ink-strong/8" />
        <div className="h-2.5 w-1/2 rounded-full bg-ink-strong/10" />
      </div>
      {/* faux cursor/tap */}
      <div
        className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-accent/70 transition-all duration-300 ease-out"
        style={{
          left: `${30 + progress * 40}%`,
          top: `${28 + progress * 46}%`,
          opacity: progress > 0.05 ? 0.9 : 0,
        }}
      />
    </div>
  );
}

/** Browser-chrome wireframe for web sessions. */
function WebWireframe({ session, progress }: { session: ReplaySession; progress: number }) {
  return (
    <div className="overflow-hidden rounded-card border border-hairline bg-canvas">
      {/* chrome */}
      <div className="flex items-center gap-2 border-b border-hairline bg-paper-2 px-3 py-2 dark:bg-white/5">
        <span className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-ink-strong/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-strong/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-strong/15" />
        </span>
        <span className="ml-2 flex-1 truncate rounded-full bg-paper px-3 py-1 font-mono text-[10px] text-subtle">
          aigen.solutions/pricing
        </span>
      </div>
      {/* viewport */}
      <div className="relative aspect-[16/10] overflow-hidden p-4">
        <div
          className="flex flex-col gap-3 transition-transform duration-200 ease-out"
          style={{ transform: `translateY(-${progress * 40}px)` }}
        >
          <div className="h-5 w-1/3 rounded-full bg-ink-strong/12" />
          <div className="h-20 rounded-lg bg-ink-strong/8" />
          <div className="grid grid-cols-3 gap-3">
            <div className="h-24 rounded-lg bg-ink-strong/8" />
            <div className="h-24 rounded-lg bg-ink-strong/10" />
            <div className="h-24 rounded-lg bg-ink-strong/8" />
          </div>
          <div
            className={
              "mt-1 h-8 w-40 rounded-lg transition-colors duration-200 " +
              (progress > 0.7 ? "bg-accent" : "bg-ink-strong/15")
            }
          />
        </div>
        <div
          className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-accent/70 transition-all duration-300 ease-out"
          style={{
            left: `${24 + progress * 50}%`,
            top: `${30 + progress * 40}%`,
            opacity: progress > 0.05 ? 0.9 : 0,
          }}
        />
        <span className="absolute right-3 top-3 font-mono text-[9px] text-subtle">
          {session.browser}
        </span>
      </div>
    </div>
  );
}

export function ReplayPlayer({ session }: { session: ReplaySession }) {
  const [t, setT] = useState(0); // seconds
  const [playing, setPlaying] = useState(true);
  const raf = useRef<number | null>(null);
  const lastTick = useRef<number>(0);
  const dur = session.durationSec;
  const markers = timelineMarkers(session);

  useEffect(() => {
    if (!playing) return;
    lastTick.current = performance.now();
    const step = (now: number) => {
      const dt = (now - lastTick.current) / 1000;
      lastTick.current = now;
      setT((prev) => {
        // play at 8× so a long session is watchable
        const next = prev + dt * 8;
        if (next >= dur) {
          setPlaying(false);
          return dur;
        }
        return next;
      });
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [playing, dur]);

  const progress = Math.min(1, t / dur);

  return (
    <div className="space-y-4">
      {/* stage */}
      <div className="rounded-card bg-paper-2 p-4 dark:bg-white/5">
        {session.kind === "mobile" ? (
          <DeviceFrame>
            <MobileWireframe progress={progress} />
          </DeviceFrame>
        ) : (
          <WebWireframe session={session} progress={progress} />
        )}
        <p className="mt-3 text-center font-mono text-[10px] text-subtle">
          {session.kind === "mobile"
            ? "mobile replay · rendered as wireframe (posthog mobile → rrweb)"
            : "web replay · rrweb dom snapshot"}
        </p>
      </div>

      {/* transport */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            if (t >= dur) setT(0);
            setPlaying((p) => !p);
          }}
          aria-label={playing ? "Pause" : "Play"}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent text-white transition-colors hover:bg-accent-hover"
        >
          {playing ? (
            <Pause className="h-4 w-4" strokeWidth={2} />
          ) : (
            <Play className="h-4 w-4 translate-x-px" strokeWidth={2} />
          )}
        </button>
        <span className="shrink-0 font-mono text-[11px] text-muted">
          {clock(t)} / {clock(dur)}
        </span>
        {/* scrubber + markers */}
        <div className="relative flex-1">
          <input
            type="range"
            min={0}
            max={dur}
            step={1}
            value={Math.floor(t)}
            onChange={(e) => {
              setT(Number(e.target.value));
              setPlaying(false);
            }}
            aria-label="Scrub replay"
            className="h-9 w-full cursor-pointer appearance-none bg-transparent"
            style={{ accentColor: "var(--accent)" }}
          />
          {/* timeline markers under the track */}
          <div className="pointer-events-none absolute inset-x-0 -bottom-0.5 h-1">
            {markers.map((m) => (
              <span
                key={m.label}
                className={
                  "absolute top-0 h-2 w-[2px] -translate-x-1/2 rounded-full " +
                  (m.rage ? "bg-accent" : "bg-ink-strong/30")
                }
                style={{ left: `${(m.at / dur) * 100}%` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* event timeline list */}
      <ul className="space-y-1.5">
        {markers.map((m) => {
          const reached = t >= m.at;
          return (
            <li
              key={m.label}
              className={
                "flex items-center gap-3 text-[13px] transition-opacity " +
                (reached ? "opacity-100" : "opacity-45")
              }
            >
              <span className="font-mono text-[11px] text-subtle">{clock(m.at)}</span>
              <span
                className={
                  "h-1.5 w-1.5 rounded-full " + (m.rage ? "bg-accent" : "bg-ink-strong/40")
                }
              />
              <span className={m.rage ? "text-accent" : "text-ink"}>{m.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
